import React from 'react';

import Heading from '@atlaskit/heading';
import CustomizeIcon from '@atlaskit/icon/core/migration/customize--preferences';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { Stack, Text } from '@atlaskit/primitives/compiled';

import type {
	ColorAndComponents,
	Iconography,
	SectionIds,
	Typography,
	VisualRefreshMode,
} from '../../types';
import ButtonGroup from '../button-group';

type VisualRefreshProps = {
	colorAndComponents: ColorAndComponents;
	iconography: Iconography;
	onColorAndComponentsChange?(colorAndComponents: ColorAndComponents): void;
	onIconographyChange?(typography: Iconography): void;
	onTypographyChange?(typography: Typography): void;
	onVisualRefreshModeChange?(visualRefreshMode: VisualRefreshMode): void;
	typography: Typography;
	visualRefreshMode: VisualRefreshMode;
	testId?: string;
	hiddenSections?: SectionIds;
};

/**
 * __Visual refresh__
 *
 * The visual refresh section of the appearance controls form.
 */
const VisualRefresh = ({
	colorAndComponents,
	iconography,
	onColorAndComponentsChange,
	onIconographyChange,
	onTypographyChange,
	onVisualRefreshModeChange,
	typography,
	visualRefreshMode,
	testId,
	hiddenSections,
}: VisualRefreshProps) => {
	return (
		<>
			<Stack space="space.050">
				<Heading size="xsmall" as="h2" id="styles">
					Styles
				</Heading>
				<Text size="small" color="color.text.subtlest">
					Preview examples with refreshed typography, icons, and colors.{' '}
					<Link href="/whats-new/atlassian-ui-refresh-updates">About the visual refresh</Link>
				</Text>
			</Stack>
			<ButtonGroup
				options={[
					{ value: 'refreshed', label: 'Refreshed' },
					{ value: 'legacy', label: 'Legacy' },
					{ value: 'custom', label: 'Custom', iconBefore: CustomizeIcon },
				]}
				selected={visualRefreshMode}
				onChange={onVisualRefreshModeChange}
				testId={testId ? `${testId}--visual-refresh-mode` : undefined}
				labelId="styles"
			/>
			{visualRefreshMode === 'custom' && (
				<>
					{!hiddenSections?.includes('typography') && (
						<>
							<Heading size="xxsmall" as="h3" id="typography">
								Typography <Lozenge appearance="new">Beta</Lozenge>
							</Heading>
							<ButtonGroup
								options={[
									{ value: 'refreshed', label: 'Refreshed' },
									{ value: 'modernized', label: 'Modernized' },
									{ value: 'adg3', label: 'Legacy' },
								]}
								selected={typography}
								onChange={onTypographyChange}
								testId={testId ? `${testId}--typography` : undefined}
								labelId="typography"
							/>
						</>
					)}
					{!hiddenSections?.includes('iconography') && (
						<>
							<Heading size="xxsmall" as="h3" id="iconography">
								Iconography <Lozenge appearance="new">Beta</Lozenge>
							</Heading>
							<ButtonGroup
								options={[
									{ value: 'refreshed', label: 'Refreshed' },
									{ value: 'legacy', label: 'Legacy' },
								]}
								selected={iconography}
								onChange={onIconographyChange}
								testId={testId ? `${testId}--iconography` : undefined}
								labelId="iconography"
							/>
						</>
					)}
					{!hiddenSections?.includes('color-and-components') && (
						<>
							<Stack space="space.050">
								<Heading size="xxsmall" as="h3" id="color-and-components">
									Color and component changes v1 <Lozenge appearance="moved">Early access</Lozenge>
								</Heading>
								<Text size="small" color="color.text.subtlest">
									We don’t recommend using this until testing is complete.
								</Text>
							</Stack>
							<ButtonGroup
								options={[
									{ value: 'refreshed', label: 'Refreshed' },
									{ value: 'legacy', label: 'Legacy' },
								]}
								selected={colorAndComponents}
								onChange={onColorAndComponentsChange}
								testId={testId ? `${testId}--color-and-components` : undefined}
								labelId="color-and-components"
							/>
						</>
					)}
				</>
			)}
		</>
	);
};

export default VisualRefresh;
